import {
  ActionIcon,
  Box,
  Loader,
  Menu,
  ScrollArea,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import { BiDotsVerticalRounded } from "react-icons/bi";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteNoteDraft, getAllDrafts } from "../../services/note";
import { setDrafts, setDraftsLoading } from "../../store/reducers/note";
import swal from "sweetalert";
import NoNoteDataSvg from "../Svgs/NoNoteDataSvg";
import CustomPagination from "../Pagination/CustomPagination";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderRadius: "16px",
    transition: "box-shadow 150ms ease",
    "& th": {
      backgroundColor: "#fff",
      color: "#000 !important",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },
  scrollArea: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
  },
  loading: {
    height: "100dvh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  noData: {
    padding: "2rem",
    textAlign: "center",
    height: "100dvh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  bg1: {
    "& td": { background: "#fff" },
    cursor: "pointer",
  },
  bg2: {
    "& td": { background: "#F9FAFB" },
    cursor: "pointer",
  },
}));

const ITEMS_PER_PAGE = 10;

const DraftNotesTab = ({ userId, onEditDraft }) => {
  const dispatch = useDispatch();
  const { drafts, draftsLoading, draftSavedAt } = useSelector((state) => state.note);
  const { allPatients } = useSelector((state) => state?.patient);
  const { allStaff } = useSelector((state) => state?.staff);
  const { classes, cx } = useStyles();
  const [scrolled, setScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getAllDrafts(dispatch, userId || null);
  }, [userId]);

  // Re-fetch silently (no loading flash) whenever a draft is auto-saved
  useEffect(() => {
    if (draftSavedAt > 0) {
      getAllDrafts(dispatch, userId || null, false);
    }
  }, [draftSavedAt]);

  // Clamp page when drafts shrink (e.g. after delete)
  const totalPages = Math.ceil((drafts?.length || 0) / ITEMS_PER_PAGE);
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const pagedDrafts = (drafts || []).slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  const handleDelete = async (draft) => {
    const confirmed = await swal({
      title: "Delete Draft?",
      text: "This draft will be permanently deleted and cannot be recovered.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;

    dispatch(setDraftsLoading(true));
    await deleteNoteDraft(draft.noteType?._id || draft.noteType, draft.client?._id || draft.client);
    dispatch(setDrafts(drafts.filter((d) => d._id !== draft._id)));
  };

  const getClientName = (draft) => {
    const clientId = draft.client?._id || draft.client;
    const patient = allPatients?.find(
      (p) => p?.id === clientId || p?._id === clientId
    );
    return patient?.name || "—";
  };

  const getStaffName = (draft) => {
    const staffId = draft.formState?.fields?.["Staff Name"];
    if (!staffId) return "—";
    const staff = allStaff?.find(
      (s) => s?.id === staffId || s?._id === staffId
    );
    return staff?.name || "—";
  };


  return (
    <Box>
      <ScrollArea
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
        className={classes.scrollArea}
      >
        {draftsLoading ? (
          <Box className={classes.loading}>
            <Loader color="yellow" />
          </Box>
        ) : drafts?.length > 0 ? (
          <Table
            fontSize="sm"
            highlightOnHover
            enableStickyHeader
            verticalSpacing="md"
            sx={{ fontFamily: "Inter" }}
          >
            <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
              <tr>
                <th style={{ minWidth: "140px" }}>Id</th>
                <th style={{ minWidth: "200px" }}>Note Type</th>
                <th style={{ minWidth: "180px" }}>Staff Name</th>
                <th style={{ minWidth: "140px" }}>Facility Name</th>
                <th style={{ minWidth: "140px" }}>Client Name</th>
                <th style={{ minWidth: "180px" }}>Last Saved</th>
                <th style={{ minWidth: "170px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedDrafts.map((draft, i) => {
                const bg = i % 2 === 0;
                return (
                  <tr key={draft._id} className={bg ? classes.bg2 : classes.bg1}>
                    <td style={{ textTransform: "uppercase" }}>
                      {draft._id?.slice(-6)}
                    </td>
                    <td>{draft.noteType?.name || "—"}</td>
                    <td>{getStaffName(draft)}</td>
                    <td>{draft.facility?.name || "—"}</td>
                    <td>{getClientName(draft)}</td>
                    <td>{moment(draft.updatedAt).format("MM/DD/YYYY hh:mm A")}</td>
                    <td>
                      <Menu shadow="md" position="bottom-end" withinPortal zIndex={1500}>
                        <Menu.Target>
                          <ActionIcon>
                            <BiDotsVerticalRounded size={20} color="#000" />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item>
                            Continue Editing
                          </Menu.Item>
                          <Menu.Item
                            color="red"
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Box className={classes.noData}>
            <NoNoteDataSvg />
            <Text fw={600} size="xl" mb="md" c="#171717" ff="inter">
              No Saved Drafts
            </Text>
            <Text fw={400} size="md" c="#A3A3A3">
              Drafts are automatically saved as you work and will appear here
              until you submit or discard them.
            </Text>
          </Box>
        )}
      </ScrollArea>

      {!draftsLoading && drafts?.length > ITEMS_PER_PAGE && (
        <CustomPagination
          data={drafts.length}
          itemsPerPage={ITEMS_PER_PAGE}
          currentPage={safePage}
          handlePageChange={(page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </Box>
  );
};

export default DraftNotesTab;
